import React, { useState, useEffect } from 'react';
import { getNextCreditNoteNumber, createCreditNote } from '../utils/api';
import { numberToWords } from '../utils/numberToWords';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import dayjs from 'dayjs';
import { UOM_OPTIONS } from '../utils/uom';
import CodeTypeSelect, { codeTypeFromIsServc, isServcFromCodeType } from './CodeTypeSelect';

const CreditNoteModal = ({ isOpen, onClose, originalInvoice, onCreditNoteCreated }) => {
  const [formData, setFormData] = useState({
    creditNoteNumber: '',
    invoiceDate: '',
    financialYear: '',
    placeOfSupply: '',
    creditReason: 'other',
    creditDescription: '',
    items: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && originalInvoice && originalInvoice.totals) {
      initializeForm();
    }
  }, [isOpen, originalInvoice]);

  const initializeForm = async () => {
    try {
      setLoading(true);
      
      // Safety check
      if (!originalInvoice || !originalInvoice.items) {
        setError('Invalid invoice data');
        return;
      }
      
      const currentDate = new Date().toISOString().split('T')[0];
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      const financialYear = `${currentYear}-${nextYear}`;

      // Get next credit note number
      const { creditNoteNumber } = await getNextCreditNoteNumber();

      // Initialize items from original invoice
      const initialItems = originalInvoice.items.map(item => ({
        ...item,
        originalAmount: item.amount,
        creditAmount: 0, // User will set this
        description: item.description,
        rate: item.rate,
        hsn: item.hsn,
        IsServc: item.IsServc || 'Y'
      }));

      setFormData({
        creditNoteNumber,
        invoiceDate: currentDate,
        financialYear,
        placeOfSupply: originalInvoice.placeOfSupply || '',
        creditReason: 'other',
        creditDescription: '',
        items: initialItems
      });
    } catch (err) {
      setError('Failed to initialize credit note form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Update credit amount based on rate and quantity
    if (field === 'rate' || field === 'qty') {
      const item = updatedItems[index];
      const creditAmount = (item.rate || 0) * (item.qty || 1);
      updatedItems[index].creditAmount = creditAmount;
      updatedItems[index].amount = -creditAmount; // Negative for credit note
    }

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let cgst = 0;
    let sgst = 0;

    formData.items.forEach(item => {
      const amount = Math.abs(item.creditAmount || 0);
      subtotal += amount;
    });

    // Calculate taxes based on original invoice rates
    if (originalInvoice && originalInvoice.totals && originalInvoice.totals.subtotal > 0) {
      const cgstRate = originalInvoice.totals.cgst / originalInvoice.totals.subtotal;
      const sgstRate = originalInvoice.totals.sgst / originalInvoice.totals.subtotal;
      cgst = subtotal * cgstRate;
      sgst = subtotal * sgstRate;
    }

    const total = subtotal + cgst + sgst;

    return { subtotal, cgst, sgst, total };
  };

  const validateForm = () => {
    if (!formData.creditNoteNumber || !formData.invoiceDate || !formData.financialYear || !formData.placeOfSupply) {
      setError('Please fill in all required fields');
      return false;
    }

    const { total } = calculateTotals();
    if (total <= 0) {
      setError('Credit note total must be greater than zero');
      return false;
    }

    if (originalInvoice && originalInvoice.totals && total > originalInvoice.totals.total) {
      setError('Credit note total cannot exceed original invoice total');
      return false;
    }

    const hasItems = formData.items.some(item => item.creditAmount > 0);
    if (!hasItems) {
      setError('Please select at least one item to credit');
      return false;
    }

    // HSN (goods) items require a UOM; SAC (service) items make it optional
    // (rent invoices predate the UOM column, so exempt them)
    const creditedHsnNoUom = originalInvoice?.invoiceType === 'rent'
      ? -1
      : formData.items.findIndex(item => item.creditAmount > 0 && (item.IsServc || 'N') !== 'Y' && !item.uom);
    if (creditedHsnNoUom !== -1) {
      setError(`Row ${creditedHsnNoUom + 1}: UOM is required for items with HSN Code.`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const { subtotal, cgst, sgst, total } = calculateTotals();

      // Filter items that have credit amounts
      const creditItems = formData.items
        .filter(item => item.creditAmount > 0)
        .map(item => ({
          description: item.description,
          rate: -Math.abs(item.rate || 0),
          amount: -Math.abs(item.creditAmount),
          hsn: item.hsn,
          IsServc: item.IsServc || 'Y',
          uom: item.uom || '',
          qty: item.qty || 1,
          unitPrice: -Math.abs(item.rate || 0),
          netAmount: -Math.abs(item.creditAmount),
          tax: item.tax || 0,
          taxAmount: -Math.abs((item.creditAmount * (item.tax || 0)) / 100),
          totalAmount: -Math.abs(item.creditAmount + ((item.creditAmount * (item.tax || 0)) / 100))
        }));

      const creditNoteData = {
        originalInvoiceId: originalInvoice._id,
        items: creditItems,
        creditReason: formData.creditReason,
        creditDescription: formData.creditDescription,
        creditNoteNumber: formData.creditNoteNumber,
        invoiceDate: formData.invoiceDate,
        financialYear: formData.financialYear,
        placeOfSupply: formData.placeOfSupply
      };

      const result = await createCreditNote(creditNoteData);
      
      onCreditNoteCreated(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create credit note');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, cgst, sgst, total } = calculateTotals();

  if (!isOpen || !originalInvoice) return null;

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 16
  };
  const modalStyle = {
    background: '#fff', borderRadius: 12, padding: 24, width: '100%',
    maxWidth: 1100, maxHeight: '95vh', overflowY: 'auto',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e5e7eb'
  };
  const titleStyle = { margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' };
  const closeBtnStyle = {
    background: 'transparent', border: 'none', color: '#6b7280',
    cursor: 'pointer', padding: 8, borderRadius: 999
  };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16,
    marginBottom: 24
  };
  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 10,
    lineHeight: 1.2,
    position: 'relative',
    zIndex: 1
  };
  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: 6, outline: 'none'
  };
  const sectionTitleStyle = { fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 12px 0' };
  const infoBoxStyle = { background: '#eff6ff', border: '1px solid #bfdbfe', padding: 16, borderRadius: 10, marginBottom: 16 };
  const tableWrapStyle = { border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto', overflowY: 'hidden', width: '100%' };
  const tableStyle = { width: '100%', minWidth: 700, borderCollapse: 'collapse', background: '#fff' };
  const thStyle = { background: '#f9fafb', textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#374151', borderBottom: '1px solid #e5e7eb' };
  const tdStyle = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' };
  const summaryBoxStyle = { background: 'linear-gradient(90deg,#eff6ff,#eef2ff)', border: '1px solid #bfdbfe', padding: 16, borderRadius: 10, marginTop: 16, marginBottom: 16 };
  const flexRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e0e7ff' };
  const actionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' };
  const btnStyle = { padding: '8px 16px', borderRadius: 8, cursor: 'pointer', border: '1px solid #d1d5db', background: '#fff', color: '#374151' };
  const primaryBtnStyle = { ...btnStyle, background: '#2563eb', border: '1px solid #2563eb', color: '#fff' };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Create Credit Note</h2>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={gridStyle}>
            <div>
              <label style={labelStyle}>Credit Note Number *</label>
              <Input name="creditNoteNumber" value={formData.creditNoteNumber} onChange={handleInputChange} required />
            </div>
            <div>
              <label style={labelStyle}>Date *</label>
              <DatePicker
                style={{ width: '100%' }}
                value={formData.invoiceDate ? dayjs(formData.invoiceDate) : null}
                onChange={(d) => setFormData(prev => ({ ...prev, invoiceDate: d ? d.format('YYYY-MM-DD') : '' }))}
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
            <div>
              <label style={labelStyle}>Financial Year *</label>
              <Input name="financialYear" value={formData.financialYear} onChange={handleInputChange} required />
            </div>
            <div>
              <label style={labelStyle}>Place of Supply *</label>
              <Input name="placeOfSupply" value={formData.placeOfSupply} onChange={handleInputChange} required />
            </div>
            <div>
              <label style={labelStyle}>Credit Reason *</label>
              <Select
                style={{ width: '100%' }}
                value={formData.creditReason}
                onChange={(val) => setFormData(prev => ({ ...prev, creditReason: val }))}
                options={[
                  { value: 'rate_difference', label: 'Rate Difference' },
                  { value: 'sales_return', label: 'Sales Return' },
                  { value: 'post_sale_discount', label: 'Post Sale Discount' },
                  { value: 'service_deficiency', label: 'Service Deficiency' },
                  { value: 'other', label: 'Other' }
                ]}
                allowClear
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <Input name="creditDescription" value={formData.creditDescription} onChange={handleInputChange} placeholder="Additional details about the credit" />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <h3 style={sectionTitleStyle}>Original Invoice: {originalInvoice?.invoiceNumber}</h3>
            <div style={infoBoxStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Client</div>
                  <div style={{ fontWeight: 600 }}>{originalInvoice?.client?.name}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Original Total</div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>₹{originalInvoice?.totals?.total?.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <h3 style={sectionTitleStyle}>Select Items to Credit</h3>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Description</th>
                    <th style={{ ...thStyle, width: 110 }}>Code Type</th>
                    <th style={{ ...thStyle, width: 110 }}>UOM</th>
                    <th style={thStyle}>Original Rate</th>
                    <th style={thStyle}>Original Amount</th>
                    <th style={thStyle}>Credit Rate</th>
                    <th style={thStyle}>Quantity</th>
                    <th style={thStyle}>Credit Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td style={tdStyle}>{item.description}</td>
                      <td style={tdStyle}>
                        <CodeTypeSelect
                          size="small"
                          value={codeTypeFromIsServc(item.IsServc)}
                          onChange={(value) => handleItemChange(index, 'IsServc', isServcFromCodeType(value))}
                        />
                      </td>
                      <td style={tdStyle}>
                        <Select
                          style={{ width: '100%' }}
                          value={item.uom || undefined}
                          onChange={(value) => handleItemChange(index, 'uom', value)}
                          placeholder="UOM"
                          options={UOM_OPTIONS}
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          getPopupContainer={(trigger) => trigger.parentNode}
                        />
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>₹{item.originalAmount?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>₹{item.originalAmount?.toLocaleString()}</td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={Math.abs(item.rate || 0)}
                          onChange={(val) => handleItemChange(index, 'rate', Math.abs(parseFloat(val || 0)))}
                          min={0}
                          step={0.01}
                          placeholder="0.00"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={item.qty || 1}
                          onChange={(val) => handleItemChange(index, 'qty', parseInt(val || 1))}
                          min={1}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: '#047857' }}>₹{item.creditAmount?.toLocaleString() || '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={summaryBoxStyle}>
            <h3 style={{ ...sectionTitleStyle, color: '#1e3a8a', marginBottom: 12 }}>Credit Note Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
              <div>
                <div style={flexRowStyle}><span style={{ fontSize: 12, color: '#374151' }}>Subtotal:</span><span style={{ fontWeight: 600 }}>₹{subtotal.toLocaleString()}</span></div>
                <div style={flexRowStyle}><span style={{ fontSize: 12, color: '#374151' }}>CGST:</span><span style={{ fontWeight: 600 }}>₹{cgst.toLocaleString()}</span></div>
                <div style={flexRowStyle}><span style={{ fontSize: 12, color: '#374151' }}>SGST:</span><span style={{ fontWeight: 600 }}>₹{sgst.toLocaleString()}</span></div>
                <div style={{ ...flexRowStyle, borderBottom: '2px solid #93c5fd', paddingTop: 12 }}><span style={{ fontWeight: 700, color: '#1e3a8a' }}>Total Credit:</span><span style={{ fontWeight: 800, color: '#1e3a8a' }}>₹{total.toLocaleString()}</span></div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>Amount in Words:</div>
                <div style={{ fontSize: 12, color: '#374151', background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #e0e7ff', fontStyle: 'italic' }}>{numberToWords(total)}</div>
              </div>
            </div>
          </div>

          <div style={actionsStyle}>
            <button type="button" onClick={onClose} disabled={loading} style={btnStyle}>Cancel</button>
            <button type="submit" disabled={loading} style={primaryBtnStyle}>{loading ? 'Creating…' : 'Create Credit Note'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditNoteModal;
